import React, { useState, useEffect } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import db from "../firebase-config.ts";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import { useUser } from './UserProvider.tsx'; 
import { useNavigate } from 'react-router-dom'; 

interface Announcement {
  id: string;
  name: string;
  link: string;
  description: string;
  photo: string;
  date: string;
}

const SponsorshipAnnouncement: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const announcementsPerPage = 5;
  const { isAuthenticated } = useUser(); // Access user authentication state
    const navigate = useNavigate(); // Initialize navigation
  
  
    useEffect(() => {
        if (!isAuthenticated) {
          navigate('/login'); // Redirect to login page
        }
      }, [isAuthenticated, navigate]);

  // Fetch Announcements from Firestore
  const fetchAnnouncements = async () => {
    const announcementsCollection = collection(db, "announcements");
    const snapshot = await getDocs(announcementsCollection);
    const announcementsData = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Announcement[];
    setAnnouncements(announcementsData);
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Add Announcement to Firestore
  const addAnnouncement = async () => {
    if (name && link && description && photo) {
      const newAnnouncement = {
        name,
        link,
        description,
        photo,
        date: new Date().toLocaleString(),
      };
      try {
        const docRef = await addDoc(collection(db, "announcements"), newAnnouncement);
        setAnnouncements((prev) => [{ id: docRef.id, ...newAnnouncement }, ...prev]);
        setName("");
        setLink("");
        setDescription("");
        setPhoto("");
      } catch (error) {
        console.error("Error adding announcement:", error);
      }
    } else {
      alert("Please fill out all fields.");
    }
  };

  // Delete Announcement from Firestore
  const deleteAnnouncement = async (id: string) => {
    try {
      await deleteDoc(doc(db, "announcements", id));
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("Error deleting announcement:", error);
    }
  };

  // Pagination Logic
  const indexOfLastAnnouncement = currentPage * announcementsPerPage;
  const indexOfFirstAnnouncement = indexOfLastAnnouncement - announcementsPerPage;
  const currentAnnouncements = announcements.slice(
    indexOfFirstAnnouncement,
    indexOfLastAnnouncement
  );
  const totalPages = Math.ceil(announcements.length / announcementsPerPage);

  return isAuthenticated ? (
    <div className="min-h-screen p-6 text-gray-200">
      <header className="mb-6">
        <h1 className="text-3xl font-bold">Sponsorship Announcements</h1>
        <p className="text-gray-400">
          Create and manage sponsorship announcements for users.
        </p>
      </header>

      {/* Add Announcement Form */}
      <div className="bg-gray-800 p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-bold mb-4">Add Announcement</h2>
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-gray-700 text-gray-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="name"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Link</label>
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            className="w-full bg-gray-700 text-gray-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="link"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full bg-gray-700 text-gray-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="description"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Photo URL</label>
          <input
            type="text"
            value={photo}
            onChange={(e) => setPhoto(e.target.value)}
            className="w-full bg-gray-700 text-gray-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="photo URL"
          />
        </div>
        <button
          onClick={addAnnouncement}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Announcement
        </button>
      </div>

      
      
    </div>
   ) : null; 
};

export default SponsorshipAnnouncement;
